// @ts-strict-ignore
import React, { useContext } from 'react';
import {
  ButtonSize,
  ButtonVariant,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import type { WorkflowOperationMessage } from '../../../store/engine/types';
import { supportedUndoTypes } from '../../../store/engine/update/constants';
import { getActionsForAssistance, workflowOperationMessagesByType } from '../../../service/assistance';
import type { AssistanceButtonAction } from '../../../service/assistance';
import {
  StyledAssistanceActions,
  StyledToolbarButton,
  StyledToolbarContainer,
  StyledToolbarMessage,
} from './styles';
import { useAppDispatch } from '~/store/reduxHooks';
import { applyPrevWorkflowDataSnapshot, clearActionRulesOperatorAmbiguousMode, dismissWorkflowOperationMessage } from '../../../store/redux/slice';
import { RuleEditorContext } from '../NodeEditorDrawer/context';
import { WorkflowOperationType } from '../../../store/engine/constants';

const AssistanceToolbar = ({
  assistanceMessageObject,
  scrollableViewMode,
}: {
  assistanceMessageObject: WorkflowOperationMessage;
  scrollableViewMode: boolean;
}) => {
  const dispatch = useAppDispatch();

  const {
    sourceAction,
    setMorphingCaretPosition,
    simpleEditorMode,
    setPendingGroupOpeningParenthesisInfo,
  } = useContext(RuleEditorContext);

  const isPreviewAction = sourceAction.type === 'preview';

  const { id, message } = assistanceMessageObject;

  const messageEligibleForUndo = supportedUndoTypes.has(message);

  const actionableMessageOptions = getActionsForAssistance(message);
  const highPriorityMessage = [
    WorkflowOperationType.AMBIGUOUS_RULE_FOUND,
  ].includes(message);

  const messageDescription = workflowOperationMessagesByType[message];

  const onUndo = () => {
    dispatch(applyPrevWorkflowDataSnapshot({
      publicId: sourceAction.publicId,
      data: assistanceMessageObject.data,
    }));
    setMorphingCaretPosition('');
  };

  const onDismiss = () => {
    dispatch(dismissWorkflowOperationMessage({ id }));
  };

  const onCustomActionClick = (action: AssistanceButtonAction) => {
    if (message === WorkflowOperationType.AMBIGUOUS_RULE_FOUND) {
      dispatch(clearActionRulesOperatorAmbiguousMode({
        id: sourceAction.publicId,
        newRuleOperatorForCorrection: action.value as 'and' | 'or',
      }));
    }

    if (message === WorkflowOperationType.CANCEL_PENDING_RULE_GROUP) {
      setPendingGroupOpeningParenthesisInfo({ path: null });
    }

    // more to come...
  };

  return (
    <StyledToolbarContainer
      isPreviewAction={isPreviewAction}
      simpleEditorMode={simpleEditorMode}
      highPriority={highPriorityMessage}
      scrollableViewMode={scrollableViewMode}
    >
      <StyledToolbarMessage>
        <Icon icon={highPriorityMessage ? IconName.Error : IconName.LightbulbHint} color="var(--cl-gray-1100)" size="small" />
        <Text size={2} weight={500}>{messageDescription}</Text>
      </StyledToolbarMessage>
      <StyledAssistanceActions
        simpleEditorMode={simpleEditorMode}
      >
        {messageEligibleForUndo && (
          <StyledToolbarButton
            icon={IconName.Undo}
            variant={ButtonVariant.tertiary}
            onClick={onUndo}
            size={ButtonSize.xsmall}
          >
            Undo
          </StyledToolbarButton>
        )}
        {actionableMessageOptions?.length > 0
          ? actionableMessageOptions.map(action => (
            <StyledToolbarButton
              variant={ButtonVariant.tertiary}
              onClick={() => onCustomActionClick(action)}
              key={action.id}
              size={ButtonSize.xsmall}
            >
              {action.name}
            </StyledToolbarButton>
          ),
          )
          : (
            <StyledToolbarButton
              icon={IconName.Xmark}
              variant={ButtonVariant.tertiary}
              onClick={onDismiss}
              size={ButtonSize.xsmall}
            >
              Dismiss message
            </StyledToolbarButton>
          )}
      </StyledAssistanceActions>
    </StyledToolbarContainer>
  );
};

export default AssistanceToolbar;
